import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 萝卜视频" description="抱歉，您查找的文章不存在或已被移除。" />
        <Navbar />
        <main className="pt-20 pb-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-500 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章中心
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, 3);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '萝卜视频',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://luoboshipin.com/' },
        { '@type': 'ListItem', position: 2, name: '文章中心', item: 'https://luoboshipin.com/articles' },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 萝卜视频`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://luoboshipin.com/articles/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogImage={article.coverImage}
        schemaData={schemaData}
      />

      <Navbar />

      <main className="pt-20 pb-16 md:pb-24 bg-background-50">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '文章中心', path: '/articles' },
                { name: article.category, path: `/articles?category=${article.categorySlug}` },
                { name: article.title },
              ]}
            />

            <div className="mt-8">
              <span className="inline-block px-3 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                {article.category}
              </span>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
              </div>

              <div className="mb-10 rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full aspect-video object-cover"
                />
              </div>

              <div
                className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                  [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                  [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                  [&_p]:mb-4 [&_p]:text-base
                  [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:space-y-1
                  [&_li]:text-base [&_li]:text-foreground-700
                  [&_strong]:text-foreground-900 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              ></div>

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-12 pt-8 border-t border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">相关文章</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/articles/${ra.slug}`}
                        className="group rounded-lg overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                      >
                        <div className="aspect-video w-full overflow-hidden">
                          <img
                            src={ra.coverImage}
                            alt={ra.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {ra.title}
                          </h4>
                          <p className="text-xs text-foreground-500 mt-2">{ra.readTime} 分钟阅读</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}